import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ChevronDown, ChevronRight, Layers, Archive, RotateCcw } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { refreshCatalogs } from "./MoneyMovementFields";
import { invalidateFamilyCache } from "./FamilySelect";
import GroupingSelect from "./GroupingSelect";
import { invalidateConceptCache } from "./ConceptSelect";

// Árbol renderizado en plano (sin recursión JSX) — jerarquía ilimitada
function buildVisibleRows(items, showArchived, collapsed) {
  const list = (items || []).filter((c) => showArchived || c.active !== false);
  const ids = new Set(list.map((i) => i.id));
  const childrenMap = {};
  const roots = [];
  list.forEach((c) => {
    if (c.parent_id && ids.has(c.parent_id)) (childrenMap[c.parent_id] = childrenMap[c.parent_id] || []).push(c);
    else roots.push(c);
  });
  Object.values(childrenMap).forEach((arr) => arr.sort((a, b) => a.name.localeCompare(b.name, "es")));
  roots.sort((a, b) => a.name.localeCompare(b.name, "es"));
  const rows = [];
  const stack = roots.map((r) => ({ node: r, depth: 0 })).reverse();
  while (stack.length) {
    const { node, depth } = stack.pop();
    const kids = childrenMap[node.id] || [];
    rows.push({ node, depth, hasKids: kids.length > 0, kidCount: kids.length });
    if (kids.length > 0 && !collapsed[node.id]) {
      for (let i = kids.length - 1; i >= 0; i--) stack.push({ node: kids[i], depth: depth + 1 });
    }
  }
  return rows;
}

export default function GroupingsTree({ kind, title, desc, testPrefix }) {
  const [items, setItems] = useState(null);
  const [newName, setNewName] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [child, setChild] = useState(null);   // { parent, name }
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/categories", { params: { kind } })
    .then((r) => setItems(r.data || []))
    .catch((e) => { toast.error(apiError(e)); setItems([]); });
  useEffect(() => { load(); }, [kind]);

  const afterChange = () => { invalidateFamilyCache(kind); invalidateConceptCache(); refreshCatalogs(); load(); };

  const createRoot = async () => {
    if (!newName.trim()) return;
    try {
      await api.post("/categories", { kind, name: newName.trim(), parent_id: null, active: true });
      toast.success("Agrupación creada");
      setNewName("");
      afterChange();
    } catch (e) { toast.error(apiError(e)); }
  };

  const createChild = async () => {
    if (!child?.name?.trim()) return;
    try {
      await api.post("/categories", { kind, name: child.name.trim(), parent_id: child.parent.id, active: true });
      toast.success(`Sub-agrupación creada bajo "${child.parent.name}"`);
      setChild(null);
      afterChange();
    } catch (e) { toast.error(apiError(e)); }
  };

  const save = async () => {
    try {
      await api.put(`/categories/${editing.id}`, { kind, name: editing.name, active: editing.active !== false, parent_id: editing.parent_id || null });
      toast.success("Actualizada");
      setEditing(null);
      afterChange();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggle = async (node, v) => {
    try {
      await api.put(`/categories/${node.id}`, { kind, name: node.name, active: v, parent_id: node.parent_id || null });
      afterChange();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (node) => {
    if (!window.confirm(`¿Eliminar la agrupación "${node.name}"?\n\nSus sub-agrupaciones y elementos vinculados pasarán a depender del nivel superior. Los movimientos históricos no se pierden.`)) return;
    try {
      await api.delete(`/categories/${node.id}`);
      toast.success("Agrupación eliminada");
      afterChange();
    } catch (e) { toast.error(apiError(e)); }
  };

  const rows = useMemo(() => buildVisibleRows(items, showArchived, collapsed), [items, showArchived, collapsed]);

  return (
    <div className="border border-border rounded-lg p-5 space-y-4" data-testid={`${testPrefix}-section`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Layers size={18} /> {title}</h3>
          <p className="text-xs text-muted-foreground">{desc}</p>
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <Switch checked={showArchived} onCheckedChange={setShowArchived} className="scale-90" />
          Ver archivadas
        </label>
      </div>
      <div className="flex gap-2">
        <Input data-testid={`${testPrefix}-new-root`} className="flex-1" placeholder="Nueva agrupación raíz…" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createRoot()} />
        <Button data-testid={`${testPrefix}-create-root`} onClick={createRoot} disabled={!newName.trim()}><Plus size={15} className="mr-1" /> Crear</Button>
      </div>
      <div className="border border-border rounded-lg py-2" data-testid={`${testPrefix}-tree`}>
        {items === null && <div className="p-4 text-sm text-muted-foreground">Cargando…</div>}
        {items !== null && rows.length === 0 && (
          <div className="p-6 text-center text-sm text-muted-foreground">Sin agrupaciones. Crea la primera arriba.</div>
        )}
        {rows.map(({ node, depth, hasKids, kidCount }) => (
          <div key={node.id} className={`flex items-center justify-between py-1.5 pr-2 rounded hover:bg-secondary/40 ${node.active === false ? "opacity-60" : ""}`} style={{ paddingLeft: depth * 22 + 8 }} data-testid={`${testPrefix}-node-${node.id}`}>
            <div className="flex items-center gap-1.5 min-w-0">
              {hasKids ? (
                <button onClick={() => setCollapsed((p) => ({ ...p, [node.id]: !p[node.id] }))} className="text-muted-foreground hover:text-foreground shrink-0" data-testid={`${testPrefix}-toggle-open-${node.id}`}>
                  {collapsed[node.id] ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                </button>
              ) : (
                <span className="w-[14px] shrink-0" />
              )}
              <span className="text-sm font-medium truncate">{node.name}</span>
              {node.active === false && <span className="text-[10px] font-bold uppercase tracking-wider bg-secondary px-1.5 py-0.5 rounded shrink-0">Archivada</span>}
              {hasKids && <span className="text-[10px] text-muted-foreground shrink-0">({kidCount})</span>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button data-testid={`${testPrefix}-add-child-${node.id}`} onClick={() => setChild({ parent: node, name: "" })} title="Añadir sub-agrupación" className="text-muted-foreground hover:text-primary"><Plus size={14} /></button>
              <button data-testid={`${testPrefix}-edit-${node.id}`} onClick={() => setEditing({ ...node })} className="text-muted-foreground hover:text-foreground"><Pencil size={14} /></button>
              {node.active !== false ? (
                <button data-testid={`${testPrefix}-archive-${node.id}`} onClick={() => toggle(node, false)} title="Archivar" className="text-muted-foreground hover:text-foreground"><Archive size={14} /></button>
              ) : (
                <button data-testid={`${testPrefix}-restore-${node.id}`} onClick={() => toggle(node, true)} title="Reactivar" className="text-muted-foreground hover:text-foreground"><RotateCcw size={14} /></button>
              )}
              <button data-testid={`${testPrefix}-delete-${node.id}`} onClick={() => remove(node)} className="text-muted-foreground hover:text-red-600"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!child} onOpenChange={(v) => !v && setChild(null)}>
        <DialogContent className="max-w-sm" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">Nueva sub-agrupación</DialogTitle></DialogHeader>
          {child && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">Se creará dentro de <span className="font-semibold text-foreground">{child.parent.name}</span>. La jerarquía no tiene límite de niveles.</p>
              <Input data-testid={`${testPrefix}-child-name`} placeholder="Nombre…" value={child.name} onChange={(e) => setChild({ ...child, name: e.target.value })} onKeyDown={(e) => e.key === "Enter" && createChild()} autoFocus />
              <Button data-testid={`${testPrefix}-child-create`} className="w-full" onClick={createChild} disabled={!child.name.trim()}>Crear</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-sm" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">Editar agrupación</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Input data-testid={`${testPrefix}-edit-name`} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} autoFocus />
              <div>
                <label className="text-xs text-muted-foreground">Depende de</label>
                <GroupingSelect kind={kind} value={editing.parent_id} onChange={(v) => setEditing({ ...editing, parent_id: v })} testId={`${testPrefix}-edit-parent`} placeholder="Raíz (sin padre)" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Activa</span>
                <Switch checked={editing.active !== false} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
              </div>
              <Button data-testid={`${testPrefix}-edit-save`} className="w-full" onClick={save} disabled={!editing.name.trim()}>Guardar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
